import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSidebarStore } from '../../store/sidebarStore';
import {
  LayoutDashboard, 
  Thermometer, 
  Radio, 
  Settings,
  PieChart,
  Map,
  Users,
  Bell,
  ChevronDown,
  ChevronRight,
  Building2,
  AlertCircle,
  Mail,
} from 'lucide-react';

const navigation = [
  {
    name: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
    isExpandable: true,
    subItems: [
      { name: 'Plan View', href: '/dashboard/plan', icon: Map }
    ]
  },
  {
    name: 'Sensors',
    icon: Thermometer,
    href: '/sensors'
  },
  { name: 'Thresholds', href: '/alarms', icon: Bell },
  { name: 'Remote Alerts', href: '/remote-alerts', icon: Mail },
  { name: 'Alarm Log', href: '/alarm-log', icon: AlertCircle },
  { name: 'Base Units', href: '/base-units', icon: Radio },
  { name: 'Zones', href: '/zones', icon: Map },
  { name: 'Analytics', href: '/analytics', icon: PieChart },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'Help', href: '/help', icon: AlertCircle },
];

export default function Sidebar() {
  const location = useLocation();
  const { isOpen, close } = useSidebarStore();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const handleLinkClick = () => {
    if (window.innerWidth < 768) {
      close();
    }
  };

  const toggleExpand = (name: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems(prev =>
      prev.includes(name) ? prev.filter(item => item !== name) : [...prev, name]
    );
  };

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity lg:hidden z-20" onClick={close} />}
      <div className={`${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-30 w-64 bg-gray-800 dark:bg-gray-900 transform transition-transform duration-200 ease-in-out`}>
        <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
          <nav className="mt-5 flex-1 px-2 space-y-1">
            {navigation.map((item) => {
              const isExpanded = expandedItems.includes(item.name);
              const isActive = location.pathname === item.href;
              const hasSubItems = item.subItems && item.subItems.length > 0;

              return (
                <div key={item.name}>
                  <div className="flex flex-col">
                    <div className="flex">
                      <Link
                        to={item.href}
                        onClick={handleLinkClick}
                        className={`${
                          isActive
                            ? 'bg-gray-900 dark:bg-gray-800 text-white'
                            : 'text-gray-300 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white'
                        } group flex-1 flex items-center px-2 py-2 text-sm font-medium rounded-l-md ${
                          hasSubItems && item.isExpandable ? 'rounded-r-none' : 'rounded-r-md'
                        }`}
                      >
                        <item.icon
                          className="mr-3 h-6 w-6 flex-shrink-0"
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>

                      {hasSubItems && item.isExpandable && (
                        <button
                          onClick={(e) => toggleExpand(item.name, e)}
                          className={`${
                            isActive
                              ? 'bg-gray-900 dark:bg-gray-800 text-white'
                              : 'text-gray-300 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white'
                          } px-2 rounded-r-md flex items-center`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>

                    {hasSubItems && isExpanded && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={handleLinkClick}
                            className={`${
                              location.pathname === subItem.href
                                ? 'bg-gray-900 dark:bg-gray-800 text-white'
                                : 'text-gray-300 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white'
                            } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
}